import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../utils/api';
import { inputCss } from '../../components/ui/FormField';
import { Num } from './components';
import { curMonth, addMonth, monthLabel, fmt, pct, pct1, buildColorMap } from './helpers';

const KIND_BADGE = { ota: 'OTA', website: 'Сайт', operator: 'Оператор', other: 'Прочее' };
const GROUP_LABEL = { direct: 'Прямые продажи', ota: 'OTA', other: 'Прочее' };
const GROUP_COLOR = { direct: '#3D8C7A', ota: '#F5644A', other: '#8A6A66' };
const groupOf = (kind) => (kind === 'ota' ? 'ota' : kind === 'other' ? 'other' : 'direct');

const VIEW_N = 12;
const fmtShort = (n) => {
  n = n || 0; const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(1).replace('.0', '') + 'М';
  if (a >= 1e3) return Math.round(n / 1e3) + 'к';
  return String(Math.round(n));
};

export default function OtaAnalytics() {
  const [month, setMonth] = useState(curMonth());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState('');
  const [metric, setMetric]   = useState('share');   // 'share' | 'revenue'
  const [viewStart, setViewStart] = useState(null);   // индекс вьюпорта в dynamics_months

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get(`/reports/ota-analytics?month=${month}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const hotels = data?.hotels || [];
  const network = data?.network;
  const dynMonths = data?.dynamics_months || [];

  const cardsForColor = [network, ...hotels].filter(Boolean);
  const colorMap = buildColorMap([
    ...cardsForColor.flatMap(h => h.channels.map(c => ({ channel_key: c.channel_key, kind: c.kind }))),
    ...cardsForColor.flatMap(h => h.dynamics.series.map(s => ({ channel_key: s.channel_key, kind: s.kind }))),
  ]);

  // Вьюпорт динамики (12 мес) — по умолчанию вокруг текущего месяца, статично.
  const maxStart = Math.max(0, dynMonths.length - VIEW_N);
  const defStart = (() => {
    const i = dynMonths.indexOf(curMonth());
    return Math.min(maxStart, Math.max(0, (i < 0 ? dynMonths.length - VIEW_N : i - 8)));
  })();
  const vs = viewStart == null ? defStart : Math.min(viewStart, maxStart);
  const viewMonths = dynMonths.slice(vs, vs + VIEW_N);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Фильтр карточек — месяц */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={lbl}>Месяц (карточки)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input style={{ ...inputCss, width: 150 }} type="month" value={month} onChange={e => setMonth(e.target.value)} />
            {[
              { label: 'Пред.', val: addMonth(curMonth(), -1) },
              { label: 'Тек.',  val: curMonth() },
              { label: 'След.', val: addMonth(curMonth(), 1) },
            ].map(b => <button key={b.label} type="button" onClick={() => setMonth(b.val)} style={presetBtn(month === b.val)}>{b.label}</button>)}
          </div>
        </div>
      </div>

      {/* Управление графиками динамики (общее для всех) */}
      {dynMonths.length > 0 && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap', paddingBottom: 4, borderBottom: '1px solid var(--border-subtle)' }}>
          <div>
            <label style={lbl}>Графики динамики</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setMetric('share')} style={presetBtn(metric === 'share')}>Доля %</button>
              <button type="button" onClick={() => setMetric('revenue')} style={presetBtn(metric === 'revenue')}>Выручка ₽</button>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Период: <b style={{ color: 'var(--text-secondary)' }}>{viewMonths.length ? `${monthLabel(viewMonths[0])} — ${monthLabel(viewMonths[viewMonths.length - 1])}` : ''}</b></label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={() => setViewStart(Math.max(0, vs - 1))} style={presetBtn(false)} disabled={vs <= 0}>◀</button>
              <input type="range" min={0} max={maxStart} value={vs} onChange={e => setViewStart(Number(e.target.value))} style={{ flex: 1 }} />
              <button type="button" onClick={() => setViewStart(Math.min(maxStart, vs + 1))} style={presetBtn(false)} disabled={vs >= maxStart}>▶</button>
            </div>
          </div>
        </div>
      )}

      {err && <p style={{ color: 'var(--status-error)' }}>{err}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && network && <HotelCard key="network" hotel={network} colorMap={colorMap} viewMonths={viewMonths} metric={metric} emphasis />}
      {!loading && hotels.map(h => (
        <HotelCard key={h.property_id} hotel={h} colorMap={colorMap} viewMonths={viewMonths} metric={metric} />
      ))}

      {!loading && (network || hotels.length > 0) && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', margin: 0 }}>
          Выручка — по активным броням TravelLine (доход пропорц. ночам брони в месяце; совпадает с «Бронями на руках» в Обзоре).
          Карточки — за выбранный месяц. Графики динамики статичны (не зависят от месяца), период листается слайдером — прошлый год … следующий.
          Комиссия OTA: «≈» — прогноз по ставке канала; «✓» — факт из акта после сведения. Эквайринг распределён на прямые каналы.
        </p>
      )}
    </div>
  );
}

function HotelCard({ hotel, colorMap, viewMonths, metric, emphasis }) {
  const { short_name, channels, totals, dynamics } = hotel;
  const [open, setOpen] = useState({});
  const css = emphasis ? { ...cardCss, borderLeft: '4px solid var(--accent)' } : cardCss;
  if (!channels.length) {
    return (
      <div style={css}>
        <div style={cardTitle}>{short_name}</div>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>Нет броней за месяц.</p>
      </div>
    );
  }
  const denom = totals.revenue || 1;
  const groups = ['direct', 'ota', 'other'].map(g => {
    const items = channels.filter(c => groupOf(c.kind) === g);
    if (!items.length) return null;
    const sum = k => items.reduce((s, c) => s + (c[k] || 0), 0);
    const revenue = sum('revenue');
    return {
      g, items, revenue, share: revenue / denom,
      commission: sum('commission'), acquiring: sum('acquiring'), net_result: sum('net_result'),
      bookings: items.reduce((s, c) => s + (c.bookings_count || 0), 0),
      margin: revenue > 0 ? sum('net_result') / revenue : null,
    };
  }).filter(Boolean).sort((a, b) => b.revenue - a.revenue);
  const toggle = g => setOpen(o => ({ ...o, [g]: !o[g] }));
  return (
    <div style={css}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={cardTitle}>{short_name}</div>
        <div style={{ display: 'flex', gap: 18, fontSize: 'var(--text-sm)' }}>
          <Stat label="Выручка" value={fmt(totals.revenue)} />
          <Stat label="Комиссия OTA" value={fmt(totals.commission)} />
          <Stat label="Эквайринг" value={fmt(totals.acquiring)} />
          <Stat label="NET" value={fmt(totals.net_result)} strong />
        </div>
      </div>

      {/* 100% стек: распределение между группами (Прямые / OTA) */}
      <StackedBar segments={groups.map(gr => ({ value: gr.revenue, color: GROUP_COLOR[gr.g] }))} />
      <Legend items={groups.map(gr => ({ color: GROUP_COLOR[gr.g], label: GROUP_LABEL[gr.g], sub: pct(gr.share) }))} />

      {/* Таблица: сворачиваемые группы → каналы */}
      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)', minWidth: 680 }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle)' }}>
              {['Канал', 'Выручка', 'Доля', 'Брони', 'Комиссия OTA', 'Эквайринг', 'NET', 'Маржа'].map((hh, i) => (
                <th key={i} style={th(i === 0)}>{hh}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(gr => (
              <React.Fragment key={gr.g}>
                <tr onClick={() => toggle(gr.g)} style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', background: 'var(--bg-subtle)' }}>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 'var(--font-semibold)' }}>
                    <span style={{ display: 'inline-block', width: 14, color: 'var(--text-muted)' }}>{open[gr.g] ? '▾' : '▸'}</span>
                    <span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 2, background: GROUP_COLOR[gr.g], margin: '0 8px', verticalAlign: 'middle' }} />
                    {GROUP_LABEL[gr.g]}
                    <span style={{ marginLeft: 8, fontSize: '0.65rem', color: 'var(--text-muted)' }}>{gr.items.length}</span>
                  </td>
                  <Num color="var(--text-primary)" bold>{fmt(gr.revenue)}</Num>
                  <Num bold>{pct(gr.share)}</Num>
                  <Num>{gr.bookings}</Num>
                  <Num>{gr.g === 'ota' ? fmt(gr.commission) : '—'}</Num>
                  <Num>{gr.g === 'direct' ? fmt(gr.acquiring) : '—'}</Num>
                  <Num color={gr.net_result >= 0 ? 'var(--status-success)' : 'var(--status-error)'} bold>{fmt(gr.net_result)}</Num>
                  <Num>{gr.margin != null ? pct1(gr.margin) : '—'}</Num>
                </tr>
                {open[gr.g] && gr.items.map(c => (
                  <tr key={c.channel_key} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '7px 12px 7px 34px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                      <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: colorMap[c.channel_key], marginRight: 8, verticalAlign: 'middle' }} />
                      {c.channel_label}<span style={badge}>{KIND_BADGE[c.kind]}</span>
                    </td>
                    <Num>{fmt(c.revenue)}</Num>
                    <Num>{pct(c.revenue / denom)}</Num>
                    <Num>{c.bookings_count || '—'}</Num>
                    <CommissionCell c={c} />
                    <Num>{c.acquiring ? fmt(c.acquiring) : '—'}</Num>
                    <Num color={c.net_result >= 0 ? 'var(--status-success)' : 'var(--status-error)'}>{fmt(c.net_result)}</Num>
                    <Num>{c.net_margin != null ? pct1(c.net_margin) : '—'}</Num>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-subtle)', fontWeight: 'var(--font-semibold)' }}>
              <td style={{ padding: '9px 12px' }}>Итого</td>
              <Num bold>{fmt(totals.revenue)}</Num>
              <Num>100%</Num>
              <Num>{channels.reduce((s, c) => s + (c.bookings_count || 0), 0)}</Num>
              <Num>{fmt(totals.commission)}</Num>
              <Num>{fmt(totals.acquiring)}</Num>
              <Num color={totals.net_result >= 0 ? 'var(--status-success)' : 'var(--status-error)'} bold>{fmt(totals.net_result)}</Num>
              <Num>{totals.revenue > 0 ? pct1(totals.net_result / totals.revenue) : '—'}</Num>
            </tr>
          </tbody>
        </table>
      </div>

      {/* График динамики каналов (во всю ширину) */}
      {viewMonths.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Динамика каналов · {metric === 'share' ? 'доля %' : 'выручка ₽'}
          </div>
          <LineChart months={viewMonths} series={dynamics.series} metric={metric} colorMap={colorMap} />
          <Legend items={dynamics.series.map(s => ({ color: colorMap[s.channel_key], label: s.channel_label }))} />
        </div>
      )}
    </div>
  );
}

// ─── 100% горизонтальный стек ─────────────────────────────────────────────────
function StackedBar({ segments, height = 30 }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0) || 1;
  let x = 0;
  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none"
      style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', display: 'block' }}>
      {segments.map((s, i) => {
        const w = 100 * Math.max(0, s.value) / total;
        const rect = <rect key={i} x={x} y={0} width={w} height={height} fill={s.color} />;
        x += w; return rect;
      })}
    </svg>
  );
}

// ─── Линейный график во всю ширину: X — месяцы, Y — доля% или выручка, линии — каналы ──
function LineChart({ months, series, metric, colorMap }) {
  const W = 1000, H = 300, padL = 52, padR = 16, padT = 12, padB = 30;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = months.length;
  const xFor = i => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const valOf = (s, ym) => { const p = s.points.find(pt => pt.ym === ym); return p ? (metric === 'share' ? p.share : p.revenue) : 0; };

  let maxY = metric === 'share' ? 1 : 0;
  if (metric === 'revenue') for (const s of series) for (const ym of months) maxY = Math.max(maxY, valOf(s, ym));
  if (maxY <= 0) maxY = 1;
  const yFor = v => padT + (1 - v / maxY) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => f * maxY);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxHeight: 340 }}>
      {/* сетка + подписи Y */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padL} y1={yFor(t)} x2={W - padR} y2={yFor(t)} stroke="var(--border-subtle)" strokeWidth="1" />
          <text x={padL - 8} y={yFor(t) + 4} textAnchor="end" fontSize="12" fill="var(--text-muted)">
            {metric === 'share' ? Math.round(t * 100) + '%' : fmtShort(t)}
          </text>
        </g>
      ))}
      {/* подписи X */}
      {months.map((ym, i) => (
        <text key={ym} x={xFor(i)} y={H - 10} textAnchor="middle" fontSize="12" fill="var(--text-muted)">{monthLabel(ym)}</text>
      ))}
      {/* линии каналов */}
      {series.map(s => {
        const pts = months.map((ym, i) => `${xFor(i)},${yFor(valOf(s, ym))}`).join(' ');
        return (
          <g key={s.channel_key}>
            <polyline points={pts} fill="none" stroke={colorMap[s.channel_key]} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            {months.map((ym, i) => (
              <circle key={ym} cx={xFor(i)} cy={yFor(valOf(s, ym))} r="3" fill={colorMap[s.channel_key]}>
                <title>{`${s.channel_label} · ${monthLabel(ym)}: ${metric === 'share' ? Math.round(valOf(s, ym) * 100) + '%' : fmt(valOf(s, ym))}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function CommissionCell({ c }) {
  if (c.kind !== 'ota') return <Num>—</Num>;
  if (c.reconciled) {
    return <Num color="var(--text-secondary)"><span style={{ color: 'var(--status-success)' }}>✓ </span>{fmt(c.commission_actual || 0)}</Num>;
  }
  return (
    <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'var(--text-muted)' }} title="Прогноз по ставке канала — до сведения месяца">
      ≈ {fmt(c.commission_estimated || 0)}
    </td>
  );
}

function Stat({ label, value, strong }) {
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <b style={{ color: strong ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: strong ? 'var(--font-bold)' : 'var(--font-semibold)' }}>{value}</b>
    </span>
  );
}

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
          <span style={{ width: 11, height: 11, borderRadius: 2, background: it.color }} />
          {it.label}{it.sub ? <b style={{ color: 'var(--text-primary)' }}>{it.sub}</b> : null}
        </span>
      ))}
    </div>
  );
}

const cardCss = { background: 'var(--surface-card)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-brand)', padding: '24px 28px' };
const cardTitle = { font: '400 16px/1.2 var(--font-family-display)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--jasper-500)' };
const lbl = { display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 4 };
const badge = { marginLeft: 8, fontSize: '0.65rem', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', padding: '1px 7px', verticalAlign: 'middle' };
const th = (left) => ({ padding: '9px 12px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: left ? 'left' : 'right', whiteSpace: 'nowrap' });
const presetBtn = (active) => ({
  padding: '8px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)',
  border: active ? '2px solid var(--accent)' : '2px solid var(--border)',
  background: active ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent',
  color: active ? 'var(--accent)' : 'var(--text-secondary)',
});
